import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { OrganizationService } from '@app/core/services/OrganizationService';
import { EditCustomerDialogComponent } from '@app/shared/customer/EditCustomerDialog.Component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-organization-recipient',
  standalone: true,
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatSelectModule, MatButtonModule, MatIconModule, MatTooltipModule],
  styles: [`:host { display:block; min-width:0; } mat-form-field { width:100%; min-width:0; }
    .recipient-field { position:relative; }
    .recipient-actions { display:flex; justify-content:flex-end; gap:8px; margin-top:8px; }
    .recipient-actions--card { position:absolute; right:-120px; top:4px; margin:0; }
    .recipient-actions button { width:48px; height:48px; border-radius:0; }
    @media (max-width:768px) { .recipient-actions--card { position:static; margin-top:8px; } }`],
  template: `
    <div class="recipient-field">
    <mat-form-field appearance="fill" floatLabel="always" [subscriptSizing]="subscriptSizing">
      <mat-label>{{label}}</mat-label>
      <mat-select [disabled]="disabled || loading" [ngModel]="value" (ngModelChange)="select($event)">
        <mat-option [value]="null">Välj organisation</mat-option>
        <mat-option *ngFor="let organization of recipientOptions()" [value]="organization.Id">
          {{customerFor(organization)?.Name || organization.Name}} ({{organization.OrgNumber}})
        </mat-option>
      </mat-select>
      <mat-hint *ngIf="loading">Laddar organisationer…</mat-hint>
      <mat-hint *ngIf="error" role="alert">{{error}}</mat-hint>
    </mat-form-field>
    <div class="recipient-actions" [class.recipient-actions--card]="customerActionsPlacement === 'card'" *ngIf="allowCustomerEditing">
      <button mat-icon-button type="button" aria-label="Lägg till mottagarorganisation som kund" matTooltip="Ny kund" [disabled]="disabled || loading" (click)="editCustomer()"><mat-icon>add</mat-icon></button>
      <button mat-icon-button type="button" aria-label="Redigera mottagarkund och betaluppgifter" matTooltip="Kund och betaluppgifter" [disabled]="disabled || loading || !selectedCustomer()" (click)="editCustomer(selectedCustomer())"><mat-icon>edit</mat-icon></button>
    </div>
    </div>`
})
export class OrganizationRecipientComponent implements OnChanges, OnDestroy {
  @Input() value: number | null = null;
  @Input() disabled = false;
  @Input() subscriptSizing: 'fixed' | 'dynamic' = 'fixed';
  @Input() label = 'Mottagarorganisation';
  @Input() invoiceId = 0;
  @Input() senderOrganizationId = 0;
  @Input() senderUserId = '';
  @Input() allowOwnOrganization = false;
  @Input() allowCustomerEditing = false;
  @Input() customerActionsPlacement: 'below' | 'card' = 'below';
  @Input() customers: any[] = [];
  @Input() customersRevision = 0;
  @Output() valueChange = new EventEmitter<number | null>();
  @Output() organizationChange = new EventEmitter<any>();
  @Output() customerSaved = new EventEmitter<any>();
  organizations: any[] = [];
  loading = true;
  error = '';
  private recipientsSubscription?: Subscription;
  private customerDialogSubscription?: Subscription;
  private pendingCustomerNumber = '';
  constructor(private organizationsService: OrganizationService, private dialog: MatDialog, private cdr: ChangeDetectorRef) {}
  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.invoiceId && !changes.senderOrganizationId && !changes.senderUserId && !changes.allowOwnOrganization && !changes.customersRevision) return;
    if (changes.invoiceId || changes.senderOrganizationId || changes.senderUserId) this.pendingCustomerNumber = '';
    this.loadRecipients();
  }
  private loadRecipients(): void {
    this.recipientsSubscription?.unsubscribe();
    this.organizations = []; this.loading = true; this.error = '';
    this.recipientsSubscription = this.organizationsService.getRecipients(this.invoiceId || 0,
      this.senderOrganizationId || 0, this.senderUserId || '', this.allowOwnOrganization).subscribe({
      next: organizations => {
        this.organizations = organizations; this.loading = false;
        if (this.pendingCustomerNumber) {
          const recipient = organizations.find(o => this.normalizeNumber(o.OrgNumber) === this.pendingCustomerNumber);
          this.pendingCustomerNumber = '';
          if (recipient) this.select(recipient.Id);
          else this.error = 'Kunden har sparats, men kan inte väljas här. Mottagaren måste ha ett organisationskonto.';
        }
        this.cdr.markForCheck();
      },
      error: () => { this.error = 'Organisationerna kunde inte hämtas. Försök igen genom att öppna vyn på nytt.'; this.loading = false; this.cdr.markForCheck(); }
    });
  }
  private normalizeNumber(value: string): string { return (value || '').replace(/[- ]/g, '').toUpperCase(); }
  customerFor(organization: any): any {
    if (!organization) return null;
    if (organization.CustomerId !== undefined)
      return (this.customers || []).find(c => c.Id === organization.CustomerId) || null;
    return [...(this.customers || [])].sort((a, b) =>
      Number(a.Id === organization.CompanyId) - Number(b.Id === organization.CompanyId) || a.Id - b.Id)
      .find(c => c.Id === organization.CompanyId || (this.normalizeNumber(c.OrgNumber) &&
        this.normalizeNumber(c.OrgNumber) === this.normalizeNumber(organization.OrgNumber))) || null;
  }
  selectedCustomer(): any { return this.customerFor(this.organizations.find(o => o.Id === this.value)); }
  recipientOptions(): any[] {
    if (!this.allowCustomerEditing) return this.organizations;
    return this.organizations.filter(o => this.customerFor(o) || (this.allowOwnOrganization && o.CustomerId === null))
      .sort((a, b) => (this.customerFor(a)?.Name || a.Name || '').localeCompare(this.customerFor(b)?.Name || b.Name || '', 'sv'));
  }
  editCustomer(customer?: any): void {
    if (!this.allowCustomerEditing || this.disabled || this.loading) return;
    const recipient = this.organizations.find(o => o.Id === this.value);
    // Never edit another organization's own company through a sender's customer card.
    const copy = customer ? JSON.parse(JSON.stringify(customer)) : null;
    if (copy && copy.Id === recipient?.CompanyId) { copy.Id = 0; copy.User = { Id: this.senderUserId }; }
    const dialogRef = this.dialog.open(EditCustomerDialogComponent, { data: {
      organizationId: this.senderOrganizationId || 0, showPaymentInfo: true,
      recipientOrganizationOnly: true, customer: copy,
      customerTemplate: { Id: 0, User: { Id: this.senderUserId }, Country: { Id: 1, Name: 'Sverige' }, IsCompany: true }
    } });
    this.customerDialogSubscription?.unsubscribe();
    this.customerDialogSubscription = dialogRef.afterClosed().subscribe(result => {
      if (!result) return;
      this.pendingCustomerNumber = this.normalizeNumber(result.OrgNumber);
      this.customerSaved.emit(result);
      this.loadRecipients();
    });
  }
  ngOnDestroy(): void { this.recipientsSubscription?.unsubscribe(); this.customerDialogSubscription?.unsubscribe(); }
  select(id: number | null): void {
    this.valueChange.emit(id);
    this.organizationChange.emit(this.organizations.find(o => o.Id === id) || null);
  }
}
